import React from "react";
import {
  Table,
  TableContainer,
  TableContainerProps,
  TableProps,
  Paper,
  SxProps,
  Theme,
} from "@mui/material";

/**
 * Helper function to get mobile edge-to-edge styles for TableContainer
 * Use this when you need more control (e.g., with drag-and-drop) or can't use MobileTable
 *
 * @param parentPadding - The padding value of the parent container in MUI spacing units (default: 2)
 * @param edgeOffset - Pixels to leave on each edge on mobile (default: 0 for true edge-to-edge)
 * @returns SxProps to spread into TableContainer's sx prop
 *
 * Usage:
 * ```tsx
 * <TableContainer sx={getMobileTableStyles(4)}>
 *   <Table>...</Table>
 * </TableContainer>
 *
 * // With 1px edge offset
 * <TableContainer sx={getMobileTableStyles(2, 1)}>
 *   <Table>...</Table>
 * </TableContainer>
 * ```
 */
export const getMobileTableStyles = (parentPadding: number = 2, edgeOffset: number = 0): SxProps<Theme> => {
  const paddingPx = parentPadding * 8;
  const totalExpansion = (paddingPx - edgeOffset) * 2;
  const marginValue = `${-(paddingPx - edgeOffset)}px`;

  return {
    mx: { xs: marginValue, sm: 0 },
    width: { xs: `calc(100% + ${totalExpansion}px)`, sm: "100%" },
    borderRadius: { xs: edgeOffset > 0 ? 1 : 0, sm: 1 },
  };
};

/**
 * MobileTable - A table component that automatically extends edge-to-edge on mobile
 *
 * This component wraps MUI's Table and TableContainer, adding responsive styling
 * that removes padding and border radius on mobile devices for a cleaner look.
 *
 * Usage:
 * ```tsx
 * <MobileTable>
 *   <TableHead>
 *     <TableRow>
 *       <TableCell>Header</TableCell>
 *     </TableRow>
 *   </TableHead>
 *   <TableBody>
 *     <TableRow>
 *       <TableCell>Content</TableCell>
 *     </TableRow>
 *   </TableBody>
 * </MobileTable>
 * ```
 *
 * For cases where you need more control (e.g., drag-and-drop), use getMobileTableStyles():
 * ```tsx
 * import { getMobileTableStyles } from "../components/MobileTable";
 *
 * <TableContainer component={Paper} sx={getMobileTableStyles(4)}>
 *   <Table>...</Table>
 * </TableContainer>
 * ```
 */

interface MobileTableProps {
  children: React.ReactNode;
  /** Parent container padding in MUI spacing units (default: 2 = 16px) */
  parentPadding?: number;
  /** Additional props for TableContainer */
  containerProps?: Omit<TableContainerProps, "sx" | "component">;
  /** Additional props for Table */
  tableProps?: TableProps;
  /** Additional sx props for TableContainer (merged with mobile styles) */
  containerSx?: SxProps<Theme>;
  /** Additional sx props for Table */
  tableSx?: SxProps<Theme>;
  /** Whether to use Paper as container component (default: true) */
  usePaper?: boolean;
  /** Paper variant when usePaper is true (default: "outlined") */
  paperVariant?: "outlined" | "elevation";
}

const MobileTable: React.FC<MobileTableProps> = ({
  children,
  parentPadding = 2,
  containerProps,
  tableProps,
  containerSx,
  tableSx,
  usePaper = true,
  paperVariant = "outlined",
}) => {
  const mobileStyles = getMobileTableStyles(parentPadding);

  return (
    <TableContainer
      component={usePaper ? Paper : "div"}
      variant={usePaper ? paperVariant : undefined}
      sx={[
        mobileStyles,
        // Merge with any additional sx props
        ...(Array.isArray(containerSx) ? containerSx : [containerSx]),
      ]}
      {...containerProps}
    >
      <Table sx={tableSx} {...tableProps}>
        {children}
      </Table>
    </TableContainer>
  );
};

export default MobileTable;
